/**
 * ═══════════════════════════════════════════════════════════════════════
 * RealtimeToastNotifier — Example Component Integration
 * ═══════════════════════════════════════════════════════════════════════
 *
 * Demonstrates how to use useRealtimeEvent to show toast notifications
 * when real-time events arrive from other users.
 *
 * This component does NOT re-render the dashboard. It only manages its
 * own tiny toast UI — zero layout shift, zero global re-renders.
 *
 * Usage:
 *   <RealtimeToastNotifier />
 *
 * Place it once inside the dashboard shell. It auto-subscribes to
 * relevant events and shows transient toasts.
 */

import React, { useState, useCallback, useRef } from 'react';
import { useRealtimeCallback } from '../../../hooks/useRealtimeEvent';
import { REALTIME_EVENTS } from '../../../services/realtime/RealtimeSyncService';
import { useTheme } from '../../../theme/ThemeContext';

const TOAST_DURATION = 4000; // ms
const MAX_TOASTS = 3;

const EVENT_MESSAGES = {
  [REALTIME_EVENTS.VISIT_CREATED]: (p) =>
    `New visit registered${p.visit?.patient_name ? `: ${p.visit.patient_name}` : ''}`,
  [REALTIME_EVENTS.VISIT_STATUS_CHANGED]: (p) =>
    `Visit ${p.previousStatus || ''}→${p.newStatus || 'updated'}`,
  [REALTIME_EVENTS.QUEUE_STARTED]: (p) =>
    `Patient called in${p.visit?.patient_name ? ` — ${p.visit.patient_name}` : ''}`,
  [REALTIME_EVENTS.QUEUE_SKIPPED]: (p) =>
    `Patient skipped from queue`,
  [REALTIME_EVENTS.VISIT_SKIPPED]: (p) =>
    `Patient skipped${p.visit?.patient_name ? ` — ${p.visit.patient_name}` : ''}`,
  [REALTIME_EVENTS.VISIT_REQUEUED]: (p) =>
    `Patient requeued${p.visit?.patient_name ? ` — ${p.visit.patient_name}` : ''} (#${p.newQueueNumber || '?'})`,
  [REALTIME_EVENTS.VISIT_UPDATED]: () => `Visit details updated`,
};

const EVENT_ICONS = {
  [REALTIME_EVENTS.VISIT_CREATED]: '📝',
  [REALTIME_EVENTS.VISIT_STATUS_CHANGED]: '🔄',
  [REALTIME_EVENTS.QUEUE_STARTED]: '▶️',
  [REALTIME_EVENTS.QUEUE_SKIPPED]: '⏭️',
  [REALTIME_EVENTS.VISIT_SKIPPED]: '⏭️',
  [REALTIME_EVENTS.VISIT_REQUEUED]: '🔄',
  [REALTIME_EVENTS.VISIT_UPDATED]: '✏️',
};

export default function RealtimeToastNotifier() {
  const { isDark } = useTheme();
  const [toasts, setToasts] = useState([]);
  const toastIdRef = useRef(0);

  const addToast = useCallback((message, icon) => {
    const id = ++toastIdRef.current;
    setToasts((prev) => {
      const next = [...prev, { id, message, icon, entering: true }];
      // Cap max toasts
      return next.length > MAX_TOASTS ? next.slice(-MAX_TOASTS) : next;
    });

    // Auto-remove after duration
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, TOAST_DURATION);
  }, []);

  // Subscribe to all relevant events (NO re-renders from this hook)
  useRealtimeCallback(
    [
      REALTIME_EVENTS.VISIT_CREATED,
      REALTIME_EVENTS.VISIT_STATUS_CHANGED,
      REALTIME_EVENTS.QUEUE_STARTED,
      REALTIME_EVENTS.QUEUE_SKIPPED,
      REALTIME_EVENTS.VISIT_SKIPPED,
      REALTIME_EVENTS.VISIT_REQUEUED,
      REALTIME_EVENTS.VISIT_UPDATED,
    ],
    (event) => {
      // Only show toasts for remote/cross-tab events (not our own actions)
      if (event.source === 'local') return;

      const messageFn = EVENT_MESSAGES[event.type];
      const message = messageFn ? messageFn(event.payload || {}) : 'Data updated';
      const icon = EVENT_ICONS[event.type] || '🔔';
      addToast(message, icon);
    }
  );

  if (toasts.length === 0) return null;

  return (
    <div
      style={{
        position: 'fixed',
        bottom: 20,
        right: 20,
        zIndex: 9999,
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        pointerEvents: 'none',
      }}
    >
      {toasts.map((toast) => (
        <div
          key={toast.id}
          style={{
            background: isDark ? 'rgba(30, 30, 40, 0.95)' : 'rgba(255, 255, 255, 0.97)',
            color: isDark ? '#e5e7eb' : '#1f2937',
            padding: '10px 16px',
            borderRadius: 10,
            fontSize: 13,
            fontWeight: 500,
            boxShadow: isDark
              ? '0 4px 20px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.08)'
              : '0 4px 20px rgba(0,0,0,0.12), 0 0 0 1px rgba(0,0,0,0.05)',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            animation: 'ak-toast-in 0.3s ease-out',
            pointerEvents: 'auto',
            maxWidth: 320,
          }}
        >
          <span style={{ fontSize: 16 }}>{toast.icon}</span>
          <span>{toast.message}</span>
        </div>
      ))}
      <style>{`
        @keyframes ak-toast-in {
          from { opacity: 0; transform: translateY(12px) scale(0.95); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>
    </div>
  );
}
